import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as Icons from 'react-feather'
import DataTable from 'react-data-table-component'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Sortable from 'sortablejs'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg'
import FinancePartnerModel from './models/FinancePartnerModel'
import FinancePartnerModal from './FinancePartnerModal'
import { getStorageValues } from '../../shared/CommonFunctions'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { logAuditEvent } from '../../utils/auditLogger'

function FinancePartnerGrid(props) {
  const [actionType, setActionType] = useState()
  const [modalIsOpen, setIsOpen] = React.useState(false)
  const [financePartnerModel, setFinancePartnerModel] = useState(new FinancePartnerModel())
  const [filterText, setFilterText] = React.useState('')
  const [alert, setAlert] = useState(null)
  const [financePartnerRows, setFinancePartnerRows] = useState([])
  const [sequenceSaving, setSequenceSaving] = useState(false)
  const tableWrapperRef = useRef(null)
  const sortableRef = useRef(null)
  const moduleName = props.moduleName || 'Finance Partners'

  useEffect(() => {
    const list = Array.isArray(props.FinancePartnerData) ? props.FinancePartnerData : []
    const sorted = [...list].sort((a, b) => Number(a.Sequance || 0) - Number(b.Sequance || 0))
    setFinancePartnerRows(sorted)
  }, [props.FinancePartnerData])

  function closeModal() {
    setIsOpen(false)
    props.fetchFinancePartners()
  }

  const rows = financePartnerRows
    ? financePartnerRows.filter((item) =>
      (item.FinancePartnerName && item.FinancePartnerName.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.ContactPerson1 && item.ContactPerson1.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.ContactPerson2 && item.ContactPerson2.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.ContactPhone1 && item.ContactPhone1.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.ContactPhone2 && item.ContactPhone2.toLowerCase().includes(filterText.toLowerCase()))
    )
    : []

  const canReorder = useMemo(() => filterText.trim().length === 0, [filterText])

  const persistSequence = async (orderedRows) => {
    try {
      setSequenceSaving(true)
      const sequenceList = orderedRows.map((row, index) => ({
        FinancePartnerId: row.FinancePartnerId,
        Sequance: index + 1
      }))
      await axios.post(getApiBaseUrl() + '/financepartner/updatesequence', {
        SequenceList: sequenceList,
        UserId: getStorageValues('userId')
      })
      CreateAlert('Sequence updated successfully', 'Success', setAlert);
      props.fetchFinancePartners();
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to update sequence', 'Error', setAlert)
    } finally {
      setSequenceSaving(false)
    }
  }

  useEffect(() => {
    const wrapper = tableWrapperRef.current
    const container = wrapper?.querySelector('.rdt_TableBody')
    if (!container) return

    if (sortableRef.current) {
      sortableRef.current.destroy()
      sortableRef.current = null
    }

    sortableRef.current = Sortable.create(container, {
      handle: '.drag-handle',
      draggable: '.rdt_TableRow',
      animation: 150,
      disabled: !canReorder || sequenceSaving,
      onEnd: (evt) => {
        if (!canReorder || evt.oldIndex === evt.newIndex) return
        setFinancePartnerRows((prev) => {
          const updated = [...prev]
          const [moved] = updated.splice(evt.oldIndex, 1)
          updated.splice(evt.newIndex, 0, moved)
          const withSequence = updated.map((row, idx) => ({ ...row, Sequance: idx + 1 }))
          persistSequence(withSequence)
          return withSequence
        })
      }
    })

    return () => {
      if (sortableRef.current) {
        sortableRef.current.destroy()
        sortableRef.current = null
      }
    }
  }, [canReorder, sequenceSaving, rows.length])

  const openActionType = (row, name) => {
    logAuditEvent(moduleName, 'View/Edit/Delete Button Click', JSON.stringify({
      Id: row?.FinancePartnerId,
      Name: row?.FinancePartnerName,
      Action: name
    }))
    setFinancePartnerModel(row)
    setIsOpen(true)
    setActionType(name)
  }

  async function onSubmit(e, data) {
    const financePartnerData = data
    if (e.target.name === 'Edit') {
      let financePartnerDetails = {
        FinancePartnerId: financePartnerData.FinancePartnerId,
        FinancePartnerName: financePartnerData.FinancePartnerName,
        InterestRate: financePartnerData.InterestRate,
        MinimumDownPayment: financePartnerData.MinimumDownPayment,
        ContactPerson1: financePartnerData.ContactPerson1,
        ContactPhone1: financePartnerData.ContactPhone1,
        ContactPerson2: financePartnerData.ContactPerson2,
        ContactPhone2: financePartnerData.ContactPhone2,
        EmailId: financePartnerData.EmailId,
        Sequance: financePartnerData.Sequance,
        UserId: getStorageValues('userId')
      }
      if (validation(financePartnerDetails)) {
        try {
          const response = await axios.post(
            getApiBaseUrl() + '/financepartner/save',
            financePartnerDetails
          )
          if (response.status === 200) {
            if (response.data.data[0]?.success === 1) {
              CreateAlert(response.data.data[0]?.message, 'Success', setAlert)
              props.fetchFinancePartners()
              closeModal()
            } else {
              CreateAlert(response.data.data[0]?.message || 'Unable to save record', 'Warning', setAlert)
            }
          }
        } catch (error) {
          console.log(error)
        }
      }
    } else if (e.target.name === 'Delete') {
      let financePartnerDetails = {
        FinancePartnerId: financePartnerModel.FinancePartnerId,
        UserId: getStorageValues('userId')
      }
      try {
        const response = await axios.post(
          getApiBaseUrl() + '/financepartner/delete',
          financePartnerDetails
        )
        if (response.status === 200) {
          CreateAlert('Finance Partner deleted successfully!!', 'Success', setAlert)
          props.fetchFinancePartners()
          closeModal()
        }
      } catch (error) {
        console.log(error)
      }
    }
  }

  function validation(financePartnerDetails) {
    let isValid = true
    if (financePartnerDetails) {
      if (financePartnerDetails.FinancePartnerName !== null) {
        if (financePartnerDetails.FinancePartnerName.length === 0) {
          CreateAlert('Please enter finance partner name!!', 'Warning', setAlert)
          isValid = false
        }
      } else {
        CreateAlert('Please enter finance partner name!!', 'Warning', setAlert)
        isValid = false
      }
    } else {
      CreateAlert('Some problem fetching user details entered by the user!!', 'Error', setAlert)
      return (isValid = false)
    }
    return isValid
  }

  const columns = [
    {
      name: <div className="titlename"></div>,
      cell: () => (
        <span className="drag-handle" style={{ cursor: canReorder ? 'grab' : 'not-allowed' }}>
          <Icons.Menu size={16} />
        </span>
      ),
      width: '30px'
    },
    {
      name: <div className="titlename"><span>Finance Partner</span></div>,
      selector: row => row.FinancePartnerName,
      cell: (row) => (
        <span title={row.FinancePartnerName}>{row.FinancePartnerName}</span>
      ),
      sortable: false,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Interest Rate</span></div>,
      selector: row => row.InterestRate,
      cell: (row) => (
        <span title={row.InterestRate}>{row.InterestRate}</span>
      ),
      sortable: false,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Min Down Payment</span></div>,
      selector: row => row.MinimumDownPayment,
      cell: (row) => (
        <span title={row.MinimumDownPayment}>{row.MinimumDownPayment}</span>
      ),
      sortable: false,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Contact 1</span></div>,
      selector: row => row.ContactPerson1,
      cell: (row) => (
        <span title={row.ContactPerson1}>{row.ContactPerson1}</span>
      ),
      sortable: false,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Phone 1</span></div>,
      selector: row => row.ContactPhone1,
      cell: (row) => (
        <span title={row.ContactPhone1}>{row.ContactPhone1}</span>
      ),
      sortable: false,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Contact 2</span></div>,
      selector: row => row.ContactPerson2,
      cell: (row) => (
        <span title={row.ContactPerson2}>{row.ContactPerson2}</span>
      ),
      sortable: false,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Phone 2</span></div>,
      selector: row => row.ContactPhone2,
      cell: (row) => (
        <span title={row.ContactPhone2}>{row.ContactPhone2}</span>
      ),
      sortable: false,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Email</span></div>,
      selector: row => row.EmailId,
      cell: (row) => (
        <span title={row.EmailId}>{row.EmailId}</span>
      ),
      sortable: false,
      sortIcon: <SortArrow />
    }
  ]

  if (props.UserPermissions.ViewModule === 1 || props.UserPermissions.EditModule === 1 || props.UserPermissions.DeleteModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          {
            props.UserPermissions.ViewModule === 1
              ? (
                <Link
                  className="me-2 p-2"
                  to=""
                  onClick={() => {
                    openActionType(row, 'View')
                  }}
                >
                  <Icons.Eye data-feather="eye" className="action-eye" />
                </Link>
              )
              : null
          }
          {
            props.UserPermissions.EditModule === 1
              ? (
                <Link
                  className="me-2 p-2"
                  to=""
                  onClick={() => {
                    openActionType(row, 'Edit')
                  }}
                >
                  <Icons.Edit data-feather="edit" className="feather-edit" />
                </Link>
              )
              : null
          }
          {
            props.UserPermissions.DeleteModule === 1
              ? (
                <Link
                  className="confirm-text p-2"
                  to=""
                  onClick={() => {
                    openActionType(row, 'Delete')
                  }}
                >
                  <Icons.Trash2 data-feather="trash-2" className="feather-trash-2" />
                </Link>
              )
              : null
          }
        </div>
      ),
      center: 'true',
      width: '150px'
    })
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card" ref={tableWrapperRef}>
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
            {!canReorder && (
              <div className="small text-muted">Clear search to reorder.</div>
            )}
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="FinancePartnerName"
            sortIcon={<SortArrow />}
            persistTableHead={true}
          />
        </div>
      </div>
      <FinancePartnerModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={financePartnerModel}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  )
}

export default FinancePartnerGrid
